// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/LeaveDashboard.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-hot-toast";
import { LayoutDashboard, Info } from "lucide-react";
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import SelectDropdown from "../../customStyles/SelectDropdown";

const LeaveDashboard = ({ user }) => {
  // --- State Management ---
  const [balances, setBalances] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(false);

  // Dynamic Year selection configuration (பழைய ஆண்டுகள் மறையாமல் இருக்க 2025-இல் இருந்து கணக்கிடப்படுகிறது)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const baseYear = 2025; // பழைய தரவுகள் மறையாமல் இருக்க அடிப்படை ஆண்டு
    const options = [];
    for (let y = baseYear; y <= currentYear + 2; y++) {
      options.push({ label: String(y), id: y });
    }
    return options;
  }, []);

  // ── FETCH CURRENT LOGGED IN USER LEAVE BALANCE ──────────────────────────────
  // ESLint எச்சரிக்கையை நீக்க useCallback பயன்படுத்தப்பட்டுள்ளது
  const fetchMyBalances = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = SummaryApi.getMyBalance?.url || "/api/hr/leave-balance/me";
      const method = SummaryApi.getMyBalance?.method || "get";
      
      const res = await api({ 
        url: `${url}?year=${selectedYear}`, 
        method 
      });

      if (res.data.success && res.data.data) {
        setBalances(res.data.data.balances || []);
      } else {
        setBalances([]);
      }
    } catch (err) {
      setBalances([]);
      toast.error(err.response?.data?.message || "Failed to load your leave balance.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchMyBalances();
  }, [fetchMyBalances]);

  return (
    <div className="p-4">
      {/* SECTION: Employee Welcome Banner */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Profile Picture இருந்தால் படம் காட்டும், இல்லையெனில் Default Icon காட்டும் */}
          {user?.profilePicture ? (
            <img 
              src={user.profilePicture} 
              alt={`${user?.firstName || "Employee"}`} 
              className="h-12 w-12 rounded-full object-cover shrink-0 border border-gray-200 shadow-sm animate-in fade-in duration-200"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center text-primary shrink-0 border border-red-100">
              <UserIcon className="w-6 h-6" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Welcome back, {user?.firstName || "Employee"}!
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Here is an overview of your leave account and balances.</p>
          </div>
        </div>

        {/* Dynamic Year Select filter */}
        <div className="w-full md:w-48">
          <SelectDropdown
            label="Fiscal Year"
            options={yearOptions}
            value={selectedYear}
            onChange={(val) => setSelectedYear(val)}
            labelKey="label"
            valueKey="id"
          />
        </div>
      </div>

      {/* SECTION: Leave Balances Grid Displays */}
      <div className="py-6">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mb-4">
          <LayoutDashboard className="w-4 h-4" />
          <span>My Leave Account Balances</span>
        </h3>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-200">
            <RefreshIcon className="w-8 h-8 text-primary animate-spin mb-3"/>
            <p className="text-sm text-gray-400 font-semibold">Retrieving balances from database...</p>
          </div>
        ) : balances.length === 0 ? (
          <div className="text-center py-16 bg-white border border-dashed border-gray-200 rounded-2xl text-gray-400 text-sm">
            No leave balances initialized for Fiscal Year {selectedYear}. Please contact HR.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {balances.map((bal, idx) => {
              const totalAllocated = (bal.allocated || 0) + (bal.carriedForward || 0);
              const remaining = totalAllocated - (bal.used || 0) - (bal.pending || 0);
              
              // Calculates percentage used for progress bars
              const percentageUsed = totalAllocated > 0 ? ((bal.used || 0) / totalAllocated) * 100 : 0;

              return (
                <div key={idx} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 space-y-4 hover:shadow transition-shadow">
                  {/* Badge Header Row */}
                  <div className="flex items-center justify-between border-b border-gray-50 pb-3">
                    <span 
                      className="px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider"
                      style={{
                        backgroundColor: `${bal.leaveTypeId?.color || "#6366f1"}15`,
                        color: bal.leaveTypeId?.color || "#6366f1",
                        border: `1px solid ${bal.leaveTypeId?.color || "#6366f1"}30`
                      }}
                    >
                      {bal.leaveTypeId?.code || "LV"}
                    </span>
                    <span className="text-sm font-bold text-gray-800">
                      {bal.leaveTypeId?.name}
                    </span>
                  </div>

                  {/* Quantitative breakdown values */}
                  <div className="grid grid-cols-4 gap-2 text-center text-xs">
                    <div className="p-2 bg-slate-50 rounded-xl">
                      <div className="text-gray-400 text-[10px] uppercase font-bold">Allocated</div>
                      <div className="font-bold text-gray-700 mt-1">{bal.allocated}</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-xl">
                      <div className="text-gray-400 text-[10px] uppercase font-bold">Carried</div>
                      <div className="font-bold text-gray-700 mt-1">{bal.carriedForward}</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-xl">
                      <div className="text-gray-400 text-[10px] uppercase font-bold">Used</div>
                      <div className="font-bold text-red-600 mt-1">{bal.used}</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-xl">
                      <div className="text-gray-400 text-[10px] uppercase font-bold">Pending</div>
                      <div className="font-bold text-yellow-600 mt-1">{bal.pending}</div>
                    </div>
                  </div>

                  {/* Dynamic Progress Bar showing leave usage ratio */}
                  <div className="space-y-1.5 pt-1">
                    <div className="flex justify-between text-[11px] text-gray-400 font-semibold">
                      <span>Usage Ratio</span>
                      <span>{percentageUsed.toFixed(0)}% Used</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500" 
                        style={{ 
                          width: `${Math.min(percentageUsed, 100)}%`,
                          backgroundColor: bal.leaveTypeId?.color || "primary"
                        }} 
                      />
                    </div>
                  </div>

                  {/* Final Net Remaining Balance Computation */}
                  <div className="flex items-center justify-between pt-2.5 border-t border-gray-100 text-xs font-bold">
                    <span className="text-gray-500">Remaining Leave Days:</span>
                    <span className="text-sm font-bold text-emerald-600">
                      {remaining.toFixed(1)} Day(s)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SECTION: Dynamic policy info box */}
      {balances.length > 0 && (
        <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex gap-3 items-start">
          <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wide">Leave Policy Disclaimer</h4>
            <p className="text-xs text-blue-800 leading-relaxed mt-1">
              Your allocations are subject to your assigned leave policy tier. Unused leave carry forwards and monthly accruals are processed automatically in alignment with your corporate payroll cycle. If you observe any discrepancies, please reach out to your HR administrator.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Mini components scoped locally to avoid external import errors
const UserIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
  </svg>
);

const RefreshIcon = ({ className, style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={className} style={style}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

export default LeaveDashboard;